import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import {
  Monitor, Shield, ShieldOff, PhoneOff, Copy, Mail,
  MousePointer2, Keyboard, CheckCircle2
} from "lucide-react";

const SAG_LOGO = "/sag-logo.png";
const ICE_SERVERS = { 
  iceServers: [
    { urls: "stun:stun.l.google.com:19302" },
    { urls: "stun:stun1.l.google.com:19302" },
    { urls: "turn:openrelay.metered.ca:80", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turn:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
    { urls: "turns:openrelay.metered.ca:443", username: "openrelayproject", credential: "openrelayproject" },
  ],
  iceTransportPolicy: "all"
};

export default function RemoteSession() {
  const { sessionCode } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const remoteVideoRef = useRef(null);
  const pcRef = useRef(null);
  const wsRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const makingOffer = useRef(false);

  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [remoteConnected, setRemoteConnected] = useState(false);
  const [controlGranted, setControlGranted] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [iceState, setIceState] = useState("new");

  // Fetch session
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await apiClient.get(`/remote-sessions/${sessionCode}`);
        setSession(res.data);
        setControlGranted(res.data.control_granted);
        setIsClient(res.data.client_id === user?.id);
      } catch {
        toast.error("Failed to join remote session");
        const dashboardRoutes = { admin: "/admin", consultant: "/consultant", field_agent: "/field-agent", client: "/client" };
        navigate(dashboardRoutes[user?.role] || "/", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
  }, [sessionCode, navigate, user?.id]);

  const createPeerConnection = useCallback((isInitiator) => {
    if (pcRef.current && pcRef.current.signalingState !== "closed") {
      pcRef.current.close();
    }
    const pc = new RTCPeerConnection(ICE_SERVERS);

    pc.onicecandidate = (event) => {
      if (event.candidate && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ice-candidate", candidate: event.candidate }));
      }
    };

    pc.ontrack = (event) => {
      console.log("[WebRTC] ontrack fired, streams:", event.streams.length, "track kind:", event.track.kind);
      const stream = event.streams[0] || new MediaStream([event.track]);
      remoteStreamRef.current = stream;
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = stream;
        remoteVideoRef.current.play?.().catch((err) => console.warn("[WebRTC] play() failed:", err));
      }
      setRemoteConnected(true);
    };

    pc.oniceconnectionstatechange = () => {
      console.log("[WebRTC] ICE state:", pc.iceConnectionState);
      setIceState(pc.iceConnectionState);
      if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
        setRemoteConnected(true);
      }
      if (pc.iceConnectionState === "disconnected" || pc.iceConnectionState === "failed" || pc.iceConnectionState === "closed") {
        setRemoteConnected(false);
      }
    };

    pc.onconnectionstatechange = () => {
      console.log("[WebRTC] Connection state:", pc.connectionState);
    };

    // Add transceiver to receive video (consultant side)
    if (!isInitiator) {
      pc.addTransceiver("video", { direction: "recvonly" });
    }

    pcRef.current = pc;
    return pc;
  }, []);

  const pendingCandidates = useRef([]);

  const sendOffer = useCallback(async () => {
    const stream = localStreamRef.current;
    if (!stream || makingOffer.current) return;
    makingOffer.current = true;
    try {
      pendingCandidates.current = [];
      const pc = createPeerConnection(true);
      stream.getTracks().forEach((track) => pc.addTrack(track, stream));
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      wsRef.current?.send(JSON.stringify({ type: "offer", sdp: pc.localDescription }));
    } catch (err) {
      console.error("Offer error:", err);
    } finally {
      makingOffer.current = false;
    }
  }, [createPeerConnection]);

  const handleSignaling = useCallback(async (data) => {
    try {
      if (data.type === "offer") {
        // Consultant receives offer from client — create new PC if needed
        let pc = pcRef.current;
        if (!pc || pc.signalingState === "closed") {
          pc = createPeerConnection(false);
        }
        await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
        
        // Flush buffered ICE candidates
        for (const c of pendingCandidates.current) {
          try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { console.warn("Stale ICE candidate skipped"); }
        }
        pendingCandidates.current = [];
        
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        wsRef.current?.send(JSON.stringify({ type: "answer", sdp: pc.localDescription }));
      } else if (data.type === "answer") {
        const pc = pcRef.current;
        if (pc && pc.signalingState === "have-local-offer") {
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          // Flush buffered ICE candidates
          for (const c of pendingCandidates.current) {
            try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) { console.warn("Stale ICE candidate skipped"); }
          }
          pendingCandidates.current = [];
        }
      } else if (data.type === "ice-candidate" && data.candidate) {
        const pc = pcRef.current;
        if (pc && pc.remoteDescription) {
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
        } else {
          // Buffer candidates until remote description is set
          pendingCandidates.current.push(data.candidate);
        }
      } else if (data.type === "control_granted") {
        setControlGranted(true);
        toast.success("Remote control granted");
      } else if (data.type === "control_revoked") {
        setControlGranted(false);
        toast.info("Remote control revoked");
      } else if (data.type === "peer_joined") {
        if (!isClient) {
          if (!pcRef.current || pcRef.current.signalingState === "closed") {
            createPeerConnection(false);
          }
        } else if (data.role === "consultant") {
          // Consultant (re)joined — re-send our screen if already sharing
          sendOffer();
        }
      } else if (data.type === "request_offer" && isClient) {
        sendOffer();
      }
    } catch (err) {
      console.error("Signaling error:", err);
    }
  }, [isClient, createPeerConnection, sendOffer]);

  // Setup WebSocket
  useEffect(() => {
    if (loading || !session) return;
    let cleanup = false;

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const wsHost = window.location.host;
    const ws = new WebSocket(`${protocol}//${wsHost}/api/ws/remote/${sessionCode}`);

    ws.onopen = () => {
      setConnected(true);
      ws.send(JSON.stringify({ type: "peer_joined", user_id: user?.id, role: isClient ? "client" : "consultant" }));
    };
    ws.onmessage = (event) => {
      if (cleanup) return;
      const msg = JSON.parse(event.data);
      handleSignaling(msg);
    };
    ws.onerror = () => {};
    ws.onclose = () => { if (!cleanup) setConnected(false); };
    wsRef.current = ws;

    // Consultant: create PC ready to receive
    if (!isClient) {
      createPeerConnection(false);
    }

    return () => {
      cleanup = true;
      localStreamRef.current?.getTracks().forEach((t) => t.stop());
      pcRef.current?.close();
      ws.close();
    };
  }, [loading, session, sessionCode, user, isClient, handleSignaling, createPeerConnection]);

  // Client: Start screen sharing
  const startScreenShare = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "always" }, audio: false });
      localStreamRef.current = stream;
      setSharing(true);

      await sendOffer();

      stream.getVideoTracks()[0].onended = () => {
        localStreamRef.current = null;
        setSharing(false);
        toast.info("Screen sharing stopped");
      };

      toast.success("Screen sharing started");
    } catch {
      toast.error("Failed to start screen sharing");
    }
  };

  const stopScreenShare = () => {
    localStreamRef.current?.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;
    pcRef.current?.close();
    setSharing(false);
    setRemoteConnected(false);
    toast.info("Screen sharing stopped");
  };

  const handleGrantControl = async () => {
    try {
      await apiClient.post(`/remote-sessions/${sessionCode}/grant-control`);
      setControlGranted(true);
      wsRef.current?.send(JSON.stringify({ type: "control_granted", user_id: user?.id }));
      toast.success("Remote control granted to consultant");
    } catch {
      toast.error("Failed to grant control");
    }
  };

  const handleRevokeControl = async () => {
    try {
      await apiClient.post(`/remote-sessions/${sessionCode}/revoke-control`);
      setControlGranted(false);
      wsRef.current?.send(JSON.stringify({ type: "control_revoked", user_id: user?.id }));
      toast.info("Remote control revoked");
    } catch {
      toast.error("Failed to revoke control");
    }
  };

  const handleEndSession = async () => {
    const dashboardRoutes = { admin: "/admin", consultant: "/consultant", field_agent: "/field-agent", client: "/client" };
    const fallbackPath = dashboardRoutes[user?.role] || "/";
    try {
      await apiClient.post(`/remote-sessions/${sessionCode}/end`);
      stopScreenShare();
      toast.success("Session ended");
    } catch {
      // ignore
    } finally {
      navigate(fallbackPath, { replace: true });
    }
  };

  const copySessionLink = () => {
    const guestUrl = window.location.origin + `/remote-join/${sessionCode}`;
    navigator.clipboard.writeText(guestUrl);
    toast.success("Guest join link copied to clipboard");
  };

  const sendLinkToClient = async () => {
    try {
      await apiClient.post(`/remote-sessions/${sessionCode}/send-link`);
      toast.success("Session link emailed to client");
    } catch {
      toast.error("Failed to send link — copy and share manually");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white animate-pulse">Connecting to session...</div>
      </div>
    );
  }

  // Client View
  if (isClient) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="remote-session-client">
        <div className="bg-slate-900/90 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={SAG_LOGO} alt="SAG" className="h-8 w-8 object-contain" />
            <div>
              <h1 className="font-heading text-lg font-bold text-white">Remote Assistance</h1>
              <p className="text-sm text-slate-400">Session: {sessionCode}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className={connected ? "bg-emerald-900 text-emerald-300" : "bg-slate-800 text-slate-400"}>
              {connected ? "Connected" : "Connecting..."}
            </Badge>
            {controlGranted && (
              <Badge variant="secondary" className="bg-amber-900 text-amber-300">
                <MousePointer2 className="h-3 w-3 mr-1" /> Control Granted
              </Badge>
            )}
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="max-w-lg w-full bg-slate-900 border-slate-800">
            <CardContent className="p-8 text-center">
              {!sharing ? (
                <>
                  <div className="w-20 h-20 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-6">
                    <Monitor className="h-10 w-10 text-sag-blue" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-white mb-2">Share Your Screen</h2>
                  <p className="text-slate-400 mb-6">Allow the consultant to view your screen for troubleshooting.</p>
                  <Button onClick={startScreenShare} className="w-full h-12 bg-sag-navy hover:bg-sag-navy-800 text-white font-semibold" data-testid="start-screen-share">
                    <Monitor className="h-5 w-5 mr-2" /> Start Screen Sharing
                  </Button>
                </>
              ) : (
                <>
                  <div className="w-20 h-20 rounded-full bg-emerald-900/50 flex items-center justify-center mx-auto mb-6 animate-pulse">
                    <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                  </div>
                  <h2 className="font-heading text-xl font-bold text-white mb-2">Screen Sharing Active</h2>
                  <p className="text-slate-400 mb-6">The consultant can now see your screen.</p>
                  <div className="bg-slate-800 rounded-xl p-4 mb-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {controlGranted ? <Shield className="h-5 w-5 text-amber-400" /> : <ShieldOff className="h-5 w-5 text-slate-500" />}
                        <div className="text-left">
                          <p className="text-white font-medium">Remote Control</p>
                          <p className="text-xs text-slate-400">{controlGranted ? "Consultant can control your mouse/keyboard" : "View only mode"}</p>
                        </div>
                      </div>
                      <Button variant={controlGranted ? "destructive" : "secondary"} size="sm"
                        onClick={controlGranted ? handleRevokeControl : handleGrantControl} data-testid="toggle-control">
                        {controlGranted ? "Revoke" : "Grant"}
                      </Button>
                    </div>
                  </div>
                  <Button variant="outline" onClick={stopScreenShare} className="w-full border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="stop-screen-share">
                    Stop Sharing
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="p-6 flex justify-center">
          <Button variant="destructive" onClick={handleEndSession} className="bg-red-500 hover:bg-red-600" data-testid="end-session">
            <PhoneOff className="h-4 w-4 mr-2" /> End Session
          </Button>
        </div>
      </div>
    );
  }

  // Consultant View
  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="remote-session-consultant">
      <div className="bg-slate-900/90 backdrop-blur border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src={SAG_LOGO} alt="SAG" className="h-8 w-8 object-contain" />
          <div>
            <h1 className="font-heading text-lg font-bold text-white">Remote Assistance</h1>
            <p className="text-sm text-slate-400">Session: {sessionCode}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="secondary" className={connected ? "bg-emerald-900 text-emerald-300" : "bg-slate-800 text-slate-400"}>
            {connected ? "Signaling: Connected" : "Signaling: Connecting..."}
          </Badge>
          <Badge variant="secondary" className={remoteConnected ? "bg-emerald-900 text-emerald-300" : "bg-slate-800 text-slate-400"}>
            WebRTC: {iceState}
          </Badge>
          {controlGranted && (
            <Badge variant="secondary" className="bg-amber-900 text-amber-300">
              <Keyboard className="h-3 w-3 mr-1" /> Control Enabled
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={copySessionLink} className="text-slate-300 hover:text-white">
            <Copy className="h-4 w-4 mr-2" /> Copy Guest Link
          </Button>
          <Button variant="ghost" size="sm" onClick={sendLinkToClient} className="text-blue-300 hover:text-white">
            <Mail className="h-4 w-4 mr-2" /> Email Link to Client
          </Button>
        </div>
      </div>

      <div className="flex-1 p-6 flex items-center justify-center">
        <div className="relative w-full max-w-5xl aspect-video bg-slate-900 rounded-2xl overflow-hidden border border-slate-800">
          <video
            ref={(el) => {
              remoteVideoRef.current = el;
              if (el && remoteStreamRef.current && el.srcObject !== remoteStreamRef.current) {
                el.srcObject = remoteStreamRef.current;
                el.play?.().catch(() => {});
              }
            }}
            autoPlay playsInline muted
            className="w-full h-full object-contain bg-black"
            data-testid="remote-screen-video"
          />
          {!remoteConnected && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-950/95 pointer-events-none">
              <div className="text-center pointer-events-auto">
                <div className="w-24 h-24 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-4 animate-pulse">
                  <Monitor className="h-12 w-12 text-slate-600" />
                </div>
                <p className="text-slate-400 mb-2">Waiting for client to share their screen...</p>
                <p className="text-slate-500 text-sm mb-3">The client has been emailed a join link. You can also share this link:</p>
                <div className="bg-slate-800 rounded-lg px-4 py-2 inline-flex items-center gap-2 cursor-pointer hover:bg-slate-700" onClick={copySessionLink}>
                  <code className="text-blue-400 text-xs">{window.location.origin}/remote-join/{sessionCode}</code>
                  <Copy className="h-3.5 w-3.5 text-slate-500" />
                </div>
              </div>
            </div>
          )}
          {controlGranted && remoteConnected && (
            <div className="absolute top-4 right-4 bg-amber-900/80 backdrop-blur rounded-lg px-3 py-2 flex items-center gap-2">
              <MousePointer2 className="h-4 w-4 text-amber-300" />
              <span className="text-sm text-amber-300">Remote control active</span>
            </div>
          )}
        </div>
      </div>

      <div className="p-6 flex justify-center">
        <div className="bg-slate-900/90 backdrop-blur rounded-full px-8 py-4 flex items-center gap-4 border border-slate-800">
          <div className="flex items-center gap-2 px-4">
            {remoteConnected ? (
              <><CheckCircle2 className="h-5 w-5 text-emerald-400" /><span className="text-sm text-slate-300">Viewing screen</span></>
            ) : (
              <><Monitor className="h-5 w-5 text-slate-500" /><span className="text-sm text-slate-500">Waiting...</span></>
            )}
          </div>
          <div className="w-px h-8 bg-slate-700" />
          <Button variant="destructive" onClick={handleEndSession} className="bg-red-500 hover:bg-red-600" data-testid="end-session-consultant">
            <PhoneOff className="h-4 w-4 mr-2" /> End Session
          </Button>
        </div>
      </div>
    </div>
  );
}
